// src/hooks/useNewsApi.tsx

import {
  useState,
  useCallback,
  createContext,
  useContext,
  useEffect,
  ReactNode,
} from 'react'

import type { AwsCollectionResponse } from '@/types/api/aws-response'
import type {
  NewsRecord,
  NewsId,
  NewsPool,
  NewsResource,
} from '@/types/domain/news'

import { authHeader, notifyError, notifySuccess } from '../api/'
import { usePreloader } from '../components/'
import { languageConstants as LC } from '../constants/'

import { refreshToken } from './auth/'

const { REACT_APP_API_URL } = import.meta.env

// pool

type NewsWritePayload = {
  data: {
    attributes: Partial<
      Pick<
        NewsRecord,
        | 'author'
        | 'content_text'
        | 'folder_id'
        | 'headline'
        | 'news_type_id'
        | 'start_time'
        | 'title'
      >
    >
    relationships: {
      contracts?: {
        data: { type: 'contract'; id: string }[]
      }
    }
  }
}

type NewsContextValue = {
  data: NewsPool
}

const NewsCtx = createContext<NewsContextValue>({ data: [] })

type Props = {
  children: ReactNode
}

const UNKNOWN_ERROR = 'Unbekannter Fehler'

export const NewsDataProvider = ({ children }: Props) => {
  const { getPool } = useNewsApi()

  const [data, setData] = useState<NewsPool>([])

  useEffect(() => {
    ;(async () => {
      try {
        const result = await getPool()
        setData(result)
      } catch (error) {
        const statusCode = error?.status ?? error?.response?.status
        notifyError(`NewsAPI: GET news ${statusCode}`)
      }
    })()
  }, [getPool])

  return <NewsCtx.Provider value={{ data }}>{children}</NewsCtx.Provider>
}

export const useNewsPool = () => useContext(NewsCtx).data

// context

const NewsTypesCtx = createContext<NewsContextValue>({ data: [] })

export const NewsTypesDataProvider = ({ children }: Props) => {
  const { getTypes } = useNewsApi()

  const [data, setData] = useState<NewsPool>([])

  useEffect(() => {
    ;(async () => {
      try {
        const result = await getTypes()
        setData(result)
      } catch (error) {
        const statusCode = error?.status ?? error?.response?.status
        notifyError(`NewsApi: GET types ${statusCode}`)
      }
    })()
  }, [getTypes])

  return (
    <NewsTypesCtx.Provider value={{ data }}>{children}</NewsTypesCtx.Provider>
  )
}

// api

export const useNewsApi = () => {
  const { start, stop } = usePreloader()

  const pool = useContext(NewsCtx)
  const types = useContext(NewsTypesCtx)

  const withRequest = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      try {
        return await fn()
      } catch (error) {
        const status = error?.status

        if (status === 401 || status === 403) {
          await refreshToken()
          return await fn()
        }

        throw {
          message:
            error?.message ||
            (typeof error === 'string' ? error : UNKNOWN_ERROR),
          status,
        }
      }
    },
    []
  )

  const withPreloader = useCallback(
    async fn => {
      start()
      try {
        return await fn()
      } finally {
        stop()
      }
    },
    [start, stop]
  )

  const getPool = useCallback(async (): Promise<NewsPool> => {
    if (pool && Array.isArray(pool.data) && pool.data.length > 0)
      return pool.data

    return withPreloader(() =>
      withRequest(async () => {
        await refreshToken()

        const url = `${REACT_APP_API_URL}/news/admin/news`
        const options = { headers: { ...authHeader() }, method: 'GET' }
        const result = await fetch(url, options)

        if (!result.ok) {
          const text = await result.text()
          throw {
            message: text || `HTTP ${result.status}`,
            status: result.status,
          }
        }

        const payload =
          (await result.json()) as AwsCollectionResponse<NewsResource>

        return payload.data ?? []
      })
    )
  }, [withRequest, pool, withPreloader])

  const getTypes = useCallback(async (): Promise<NewsPool> => {
    if (types && Array.isArray(types.data) && types.data.length > 0)
      return types.data

    return withPreloader(() =>
      withRequest(async () => {
        await refreshToken()

        const url = `${REACT_APP_API_URL}/news/admin/news-types`
        const options = { headers: { ...authHeader() }, method: 'GET' }
        const result = await fetch(url, options)

        if (!result.ok) {
          const text = await result.text()
          throw {
            message: text || `HTTP ${result.status}`,
            status: result.status,
          }
        }

        const payload =
          (await result.json()) as AwsCollectionResponse<NewsResource>

        return payload.data ?? []
      })
    )
  }, [withPreloader, withRequest, types])

  const getById = useCallback(
    async (id: NewsId): Promise<NewsResource | null> =>
      withPreloader(() =>
        withRequest(async () => {
          if (!id) return null
          await refreshToken()

          const url = `${REACT_APP_API_URL}/news/admin/news/${id}?include=contracts`
          const options = { headers: { ...authHeader() }, method: 'GET' }
          const result = await fetch(url, options)

          if (!result.ok) {
            const text = await result.text()
            throw {
              message: text || `HTTP ${result.status}`,
              status: result.status,
            }
          }

          const payload = (await result.json()) as { data: NewsResource }

          return payload.data
        })
      ),
    [withRequest, withPreloader]
  )

  type SelectRef = { value: string }

  type NewsFormInput = Partial<
    Pick<
      NewsRecord,
      | 'author'
      | 'content_text'
      | 'folder_id'
      | 'headline'
      | 'news_type_id'
      | 'start_time'
      | 'title'
    >
  > & {
    contract_id?: SelectRef[]
  }

  const save = useCallback(
    (data: NewsFormInput) =>
      withRequest(async () => {
        const {
          author,
          content_text,
          contract_id,
          folder_id,
          headline,
          news_type_id,
          start_time,
          title,
        } = data || {}

        if (!title && !content_text) throw new Error(LC.NEWS_CONTENT_REQUIRED)
        if (!folder_id && !contract_id) throw new Error(LC.NEWS_FOLDER_REQUIRED)

        const record: NewsWritePayload = {
          data: {
            attributes: {
              author,
              content_text,
              folder_id,
              headline,
              news_type_id,
              start_time,
              title,
            },
            relationships: {
              contracts: {
                data: (contract_id || []).map(item => ({
                  type: 'contract',
                  id: item.value,
                })),
              },
            },
          },
        }

        await refreshToken()

        const url = `${REACT_APP_API_URL}/news/admin/news`
        const options = {
          method: 'POST',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify(record),
        }
        const result = await fetch(url, options)

        if (!result.ok) {
          const text = await result.text()
          throw {
            message: text || `HTTP ${result.status}`,
            status: result.status,
          }
        }

        const payload = await result.json()
        notifySuccess(LC.NEWS_SAVED)

        return payload?.id ?? null
      }),
    [withRequest]
  )

  const update = useCallback(
    (id: NewsId, data) =>
      withRequest(async () => {
        const {
          author,
          content_text,
          contract_id,
          folder_id,
          headline,
          news_type_id,
          start_time,
          title,
        } = data || {}

        if (!title && !content_text) throw new Error(LC.NEWS_CONTENT_REQUIRED)
        if (!folder_id && !contract_id) throw new Error(LC.NEWS_FOLDER_REQUIRED)

        const record: NewsWritePayload = {
          data: {
            attributes: {
              author,
              content_text,
              folder_id,
              headline,
              news_type_id,
              start_time,
              title,
            },
            relationships: {
              contracts: {
                data: (contract_id || []).map(item => ({
                  type: 'contract',
                  id: item.value,
                })),
              },
            },
          },
        }

        await refreshToken()

        const url = `${REACT_APP_API_URL}/news/admin/news/${id}`
        const options = {
          method: 'PATCH',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify(record),
        }
        const result = await fetch(url, options)

        if (!result.ok) {
          const text = await result.text()
          throw {
            message: text || `HTTP ${result.status}`,
            status: result.status,
          }
        }

        const payload = await result.json()
        notifySuccess(LC.NEWS_UPDATED)

        return payload?.id ?? id
      }),
    [withRequest]
  )

  const remove = useCallback(
    (id: NewsId) =>
      withRequest(async () => {
        if (!id) throw new Error(LC.NEWS_ID_REQUIRED)

        await refreshToken()

        const url = `${REACT_APP_API_URL}/news/admin/news/${id}`
        const options = {
          method: 'DELETE',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
        }
        const result = await fetch(url, options)

        if (!result.ok) {
          const text = await result.text()
          throw {
            message: text || `HTTP ${result.status}`,
            status: result.status,
          }
        }

        notifySuccess(LC.NEWS_DELETED)

        return true
      }),
    [withRequest]
  )

  return {
    getPool,
    getById,
    getTypes,
    save,
    update,
    remove,
  }
}
